import { useApi } from '@classgo/app-kit';
import { createSedeInputSchema, updateSedeInputSchema } from '@classgo/shared';
import { Checkbox, Input } from '@classgo/ui';

import { academicApi } from '../academic/api';
import type { SedeView } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

const text = (value: string) => (value.trim() === '' ? undefined : value.trim());

/**
 * Create or edit a sede.
 */
export const SedeForm = ({ current, onDone }: { current: SedeView | null; onDone: () => void }) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateSedeInputSchema : createSedeInputSchema,
    initial: {
      name: current?.name ?? '',
      address: current?.address ?? '',
      daneSedeCode: current?.daneSedeCode ?? '',
      isMain: current?.isMain ?? false,
    },
    toInput: (values) => ({
      name: values.name,
      address: text(values.address),
      daneSedeCode: text(values.daneSedeCode),
      isMain: values.isMain,
    }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('sedes', current.id, input);
            else await academicApi(api).create('sedes', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Input
        label={t('sedes.field.name')}
        required
        value={form.values.name}
        onChange={(event) => form.set('name', event.target.value)}
        {...(form.fieldError('name') ? { error: form.fieldError('name') } : {})}
      />
      <Input
        label={t('sedes.field.address')}
        value={form.values.address}
        onChange={(event) => form.set('address', event.target.value)}
        {...(form.fieldError('address') ? { error: form.fieldError('address') } : {})}
      />
      <Input
        label={t('sedes.field.daneSedeCode')}
        help={t('sedes.field.daneSedeCode.help')}
        value={form.values.daneSedeCode}
        onChange={(event) => form.set('daneSedeCode', event.target.value)}
        {...(form.fieldError('daneSedeCode') ? { error: form.fieldError('daneSedeCode') } : {})}
      />
      <Checkbox
        label={t('sedes.field.isMain')}
        description={t('sedes.field.isMain.help')}
        checked={form.values.isMain}
        onChange={(event) => form.set('isMain', event.target.checked)}
      />
    </FormShell>
  );
};
